import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Menu, MenuItem } from "@material-ui/core";
import PropTypes from "prop-types";
import ArrowDownIcon from "./ArrowDownIcon";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    userSelect: "none",
  },
  icon: {
    width: 16,
    height: 16,
    color: "#495057",
  },
  text: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#495057",
    lineHeight: "20px",
  },
  arrow: {
    width: 12,
    height: 12,
    color: "#495057",
    marginLeft: theme.spacing(0.5),
  },
  menuPaper: {
    borderRadius: 8,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    border: "1px solid #E8E8F0",
    minWidth: 200,
    marginTop: 8,
    overflow: "hidden",
  },
  menuItem: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
    fontWeight: 500,
    fontStyle: "normal",
    color: "#575B6D",
    lineHeight: "100%",
    letterSpacing: 0,
    padding: "12px 20px",
    borderBottom: "1px solid #D5E0EB",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: "#E4DAFF80",
    },
  },
  menuItemSelected: {
    backgroundColor: "#E4DAFF80 !important",
  },
}));

/**
 * LocationSelector Component
 * 
 * A location selector dropdown component with building icon
 * 
 * @param {string} location - Current location text
 * @param {Array} stores - Store list for dropdown
 * @param {Function} onSelect - Store select handler
 * @param {boolean} disabled - Disable interaction
 * @param {string} className - Additional CSS classes
 */
const LocationSelector = ({ location, stores, onSelect, disabled, className }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const containerRef = React.useRef(null);

  const handleOpen = () => {
    if (!disabled && stores.length > 0) {
      setAnchorEl(anchorEl ? null : containerRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (store) => {
    if (onSelect) {
      onSelect(store);
    }
    handleClose();
  };

  return (
    <div
      ref={containerRef}
      className={`${classes.container} ${className || ""}`}
      onClick={handleOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen();
        }
      }}
      style={disabled ? { cursor: "default", opacity: 0.8 } : undefined}
      aria-label="Location selector"
    >
      <svg
        className={classes.icon}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
          fill="currentColor"
        />
      </svg>
      <Typography className={classes.text}>{location}</Typography>
      <ArrowDownIcon className={classes.arrow} />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ className: classes.menuPaper }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {stores.map((store) => (
          <MenuItem
            key={store.id}
            onClick={() => handleSelect(store)}
            className={classes.menuItem}
            classes={{ selected: classes.menuItemSelected }}
            selected={store.name === location}
            disableRipple
          >
            {store.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

LocationSelector.propTypes = {
  location: PropTypes.string.isRequired,
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

LocationSelector.defaultProps = {
  stores: [],
  onSelect: null,
  disabled: false,
  className: "",
};

export default LocationSelector;

